/**
 * Lightweight consent state for Google Consent Mode v2 + tag gating.
 *
 * Storage: localStorage key `_evo_consent`.
 * Default: all marketing/analytics storage denied (UK/EU friendly).
 * Essential functionality (forms, portal, error reporting) does NOT depend on
 * consent — only marketing/analytics loaders consult this module.
 */

export type ConsentValue = "granted" | "denied";

export interface ConsentState {
  ad_storage: ConsentValue;
  analytics_storage: ConsentValue;
  ad_user_data: ConsentValue;
  ad_personalization: ConsentValue;
  /** ISO timestamp of last user choice (undefined = no choice yet) */
  decided_at?: string;
}

const STORAGE_KEY = "_evo_consent";

const DEFAULT_DENIED: ConsentState = {
  ad_storage: "denied",
  analytics_storage: "denied",
  ad_user_data: "denied",
  ad_personalization: "denied",
};

const ALL_GRANTED: Omit<ConsentState, "decided_at"> = {
  ad_storage: "granted",
  analytics_storage: "granted",
  ad_user_data: "granted",
  ad_personalization: "granted",
};

const CONSENT_EVENT = "evo:consent-changed";

function safeParse(raw: string | null): ConsentState | null {
  if (!raw) return null;
  try {
    const v = JSON.parse(raw);
    if (v && typeof v === "object") return v as ConsentState;
  } catch {
    /* ignore */
  }
  return null;
}

export function getConsentState(): ConsentState {
  if (typeof window === "undefined") return { ...DEFAULT_DENIED };
  return safeParse(localStorage.getItem(STORAGE_KEY)) ?? { ...DEFAULT_DENIED };
}

export function hasUserDecided(): boolean {
  const s = getConsentState();
  return Boolean(s.decided_at);
}

export function setConsentState(partial: Partial<ConsentState>, opts: { decided?: boolean; source?: string } = {}): ConsentState {
  const previous = getConsentState();
  const next: ConsentState = { ...previous, ...partial };
  if (opts.decided !== false) next.decided_at = new Date().toISOString();
  if (typeof window !== "undefined") {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    // Push to gtag (Consent Mode v2)
    try {
      (window as any).gtag?.("consent", "update", {
        ad_storage: next.ad_storage,
        analytics_storage: next.analytics_storage,
        ad_user_data: next.ad_user_data,
        ad_personalization: next.ad_personalization,
      });
    } catch {
      /* never block */
    }
    window.dispatchEvent(new CustomEvent(CONSENT_EVENT, { detail: next }));
    // Server-side audit log — best-effort, never blocks.
    if (opts.decided !== false) {
      logConsentEvent(next, opts.source).catch(() => undefined);
    }
  }
  return next;
}

async function logConsentEvent(state: ConsentState, source?: string): Promise<void> {
  try {
    const { supabase } = await import("@/integrations/supabase/client");
    let anonId = localStorage.getItem("_evo_anon_sid");
    if (!anonId) {
      anonId = (crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`);
      localStorage.setItem("_evo_anon_sid", anonId);
    }
    await supabase.from("consent_events" as never).insert({
      anon_session_id: anonId,
      ad_storage: state.ad_storage,
      analytics_storage: state.analytics_storage,
      ad_user_data: state.ad_user_data,
      ad_personalization: state.ad_personalization,
      source: source ?? "banner",
      page_path: typeof window !== "undefined" ? window.location.pathname : null,
      consent_version: "v2",
      user_agent: typeof navigator !== "undefined" ? navigator.userAgent.slice(0, 500) : null,
    } as never);
  } catch {
    /* never block */
  }
}

export function acceptAllConsent(): ConsentState {
  return setConsentState(ALL_GRANTED);
}

export function rejectAllConsent(): ConsentState {
  return setConsentState({ ...DEFAULT_DENIED });
}

export function onConsentChange(handler: (s: ConsentState) => void): () => void {
  if (typeof window === "undefined") return () => undefined;
  const listener = (e: Event) => handler((e as CustomEvent<ConsentState>).detail ?? getConsentState());
  window.addEventListener(CONSENT_EVENT, listener);
  return () => window.removeEventListener(CONSENT_EVENT, listener);
}

export function analyticsAllowed(): boolean {
  return getConsentState().analytics_storage === "granted";
}

export function adsAllowed(): boolean {
  return getConsentState().ad_storage === "granted";
}

/**
 * Push the Consent Mode v2 default to gtag BEFORE any tag loads.
 * Call once from main.tsx — safe to call multiple times.
 */
export function installConsentDefault(): void {
  if (typeof window === "undefined") return;
  const w = window as any;
  w.dataLayer = w.dataLayer || [];
  if (!w.gtag) {
    // eslint-disable-next-line prefer-rest-params
    w.gtag = function () { w.dataLayer.push(arguments); };
  }
  const current = getConsentState();
  try {
    w.gtag("consent", "default", {
      ad_storage: current.ad_storage,
      analytics_storage: current.analytics_storage,
      ad_user_data: current.ad_user_data,
      ad_personalization: current.ad_personalization,
      wait_for_update: 500,
      region: ["GB", "EU"],
    });
  } catch {
    /* never block */
  }
}
